import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    grecaptcha?: {
      render: (
        el: HTMLElement,
        params: {
          sitekey: string;
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => number;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId?: number) => string;
    };
    onRecaptchaApiLoad?: () => void;
  }
}

/**
 * Google reCAPTCHA v2 ("I'm not a robot") site key, injected at build time.
 * When unset (typical for local/dev), the widget is not rendered and callers
 * should treat verification as disabled — mirroring the server-side verifier,
 * which is a no-op when no secret is configured.
 */
export const RECAPTCHA_SITE_KEY: string | undefined =
  (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined)?.trim() || undefined;

const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit&onload=onRecaptchaApiLoad';

let scriptPromise: Promise<void> | null = null;

/** Load the reCAPTCHA API script once and resolve when `grecaptcha` is ready. */
function loadRecaptchaApi(): Promise<void> {
  if (window.grecaptcha?.render) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    window.onRecaptchaApiLoad = () => resolve();
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface RecaptchaProps {
  /** Fired with the response token when the challenge is solved. */
  onVerify: (token: string) => void;
  /** Fired when the token expires or errors — clears the previously issued token. */
  onExpire?: () => void;
  className?: string;
}

/**
 * Renders the reCAPTCHA v2 checkbox widget. Mirrors the legacy
 * `<div class="g-recaptcha" data-sitekey="…">` in `auth/register.blade.php`.
 * Renders nothing when {@link RECAPTCHA_SITE_KEY} is unset.
 */
export function Recaptcha({ onVerify, onExpire, className }: RecaptchaProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    let cancelled = false;

    loadRecaptchaApi()
      .then(() => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
        widgetIdRef.current = window.grecaptcha!.render(containerRef.current, {
          sitekey: RECAPTCHA_SITE_KEY!,
          callback: (token) => onVerify(token),
          'expired-callback': () => onExpire?.(),
          'error-callback': () => onExpire?.(),
        });
      })
      .catch(() => {
        /* Non-fatal: script blocked/offline. Verification simply won't complete. */
      });

    return () => {
      cancelled = true;
    };
    // Intentionally run once — the widget manages its own lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!RECAPTCHA_SITE_KEY) return null;
  return <div ref={containerRef} className={className} />;
}
